import type { PrimarySelection, TreeSelection } from '../types/filters';

/**
 * Enforces the selection limit on primary selections
 */
export const enforceMaxSelections = (
  primarySelections: PrimarySelection[],
  limit: number
): PrimarySelection[] =>
  primarySelections.length <= limit
    ? primarySelections
    : primarySelections.slice(0, limit);

/**
 * Removes a selection while keeping at least one
 */
export const removeSelection = (
  primarySelections: PrimarySelection[],
  selectionIdToRemove: string
): {
  canRemove: boolean;
  updatedSelections: PrimarySelection[];
} => {
  if (primarySelections.length <= 1) {
    return { canRemove: false, updatedSelections: primarySelections };
  }

  return {
    canRemove: true,
    updatedSelections: primarySelections.filter(
      (sel) => sel.id !== selectionIdToRemove
    ),
  };
};

/**
 * Extracts the leaf (entity-level) ID from a nested tree ID.
 * e.g. "8_263_100525442" → "100525442", "100525442" → "100525442"
 */
const getLeafId = (id: string): string => {
  const parts = id.split('_');
  return parts[parts.length - 1] ?? id;
};

/**
 * Builds a unique key from a selection's leaf ID and level.
 * Leaf IDs alone can collide across tree levels (e.g. country id "1" vs
 * MSA with path "5_4_1" both produce leaf "1"). Including levelId
 * prevents those false matches while still allowing reclassified entities
 * (same level, different parent path) to be recognised.
 */
export const getPrimarySelectionMatchKey = (s: {
  id: string;
  levelId: string;
}): string => `${s.levelId}:${getLeafId(s.id)}`;

/**
 * Reads primary selections out of a persisted payload (deeplink, saved filter
 * set). Payloads written before the entity-selection rework carry an `isActive`
 * flag; only the active entries were ever charted, so the inactive ones are
 * dropped and the flag is discarded.
 */
export const readPersistedPrimarySelections = (
  persisted: (TreeSelection & { isActive?: boolean })[] | undefined
): PrimarySelection[] => {
  if (!persisted?.length) return [];
  const active = persisted.filter((sel) => sel.isActive !== false);
  const kept = active.length > 0 ? active : persisted;
  return kept.map(({ isActive: _isActive, ...selection }) => selection);
};

export const updatePrimarySelections = (
  newSelections: TreeSelection[],
  currentSelections: PrimarySelection[],
  limit: number,
  // When true, newly added selections are appended after existing ones instead
  // of prepended, so the first-selected chip keeps its leading position.
  appendLast = false
): PrimarySelection[] => {
  // Build lookup by level-qualified leaf ID so reclassified entities
  // (same company, different industry path) are recognised as existing
  // selections, without colliding across different tree levels.
  const currentKeySet = new Set(
    currentSelections.map(getPrimarySelectionMatchKey)
  );
  const newKeySet = new Set(newSelections.map(getPrimarySelectionMatchKey));

  const added = newSelections.filter(
    (s) => !currentKeySet.has(getPrimarySelectionMatchKey(s))
  );

  // Keep existing selections that are still present in newSelections,
  // but update their id/name/levelId to match the latest tree data.
  const kept = currentSelections
    .filter((s) => newKeySet.has(getPrimarySelectionMatchKey(s)))
    .map((s) => {
      const key = getPrimarySelectionMatchKey(s);
      const updated = newSelections.find(
        (ns) => getPrimarySelectionMatchKey(ns) === key
      );
      if (updated && updated.id !== s.id) {
        return {
          ...s,
          id: updated.id,
          name: updated.name,
          levelId: updated.levelId,
        };
      }
      return s;
    });

  const combined = enforceMaxSelections(
    appendLast ? [...kept, ...added] : [...added, ...kept],
    limit
  );

  // A primary filter always keeps at least one selection.
  return combined.length > 0 ? combined : currentSelections;
};

const sameSelectionIds = (a: TreeSelection[], b: TreeSelection[]): boolean =>
  a.length === b.length && a.every((selection, i) => selection.id === b[i]?.id);

/**
 * Saved default selections that are just a coded preset seed one entity, like
 * the preset itself; a hand-picked set is kept in full.
 */
export const pickFirstWhenMatchingPreset = (
  saved: TreeSelection[],
  presets: TreeSelection[][]
): TreeSelection[] =>
  presets.some((preset) => sameSelectionIds(preset, saved))
    ? saved.slice(0, 1)
    : saved;
